import { toSiteUrl } from "~~/shared/utils/seo/site";

const DEFAULT_SOCIAL_FALLBACK_IMAGE = "/images/bg_opening.png";

type UseSocialSeoOptions = {
  fallbackImagePath?: string;
};

function normalizeFallbackPath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_SOCIAL_FALLBACK_IMAGE;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function useSocialSeo(options: UseSocialSeoOptions = {}) {
  const { SITE_URL } = usePublicUrls();
  const fallbackImagePath = normalizeFallbackPath(
    options.fallbackImagePath ?? DEFAULT_SOCIAL_FALLBACK_IMAGE,
  );

  function humanizeSlugLabel(slug: string, fallback: string): string {
    const trimmed = slug.trim();
    if (!trimmed) return fallback;

    const chunks = trimmed.split("-");
    const withoutId =
      chunks.length > 1 ? chunks.slice(0, -1).join("-") : trimmed;
    const normalized = withoutId.replace(/[-_]+/g, " ").trim();
    if (!normalized) return fallback;

    return normalized
      .split(" ")
      .map((token) =>
        token ? token.charAt(0).toUpperCase() + token.slice(1) : "",
      )
      .join(" ");
  }

  function normalizeMetaText(input: string): string {
    return input.replace(/\s+/g, " ").trim();
  }

  function getPublicOrigin(): string {
    return SITE_URL;
  }

  function toAbsolutePublicUrl(input: string | null | undefined): string {
    const origin = getPublicOrigin();
    const value = (input ?? "").trim();

    if (!value) {
      return `${origin}${fallbackImagePath}`;
    }
    if (/^data:/i.test(value)) {
      return `${origin}${fallbackImagePath}`;
    }
    if (value.startsWith("//")) {
      return `https:${value}`;
    }
    if (/^https?:\/\//i.test(value)) {
      try {
        const parsed = new URL(value);
        if (parsed.protocol === "http:") parsed.protocol = "https:";
        return parsed.toString();
      } catch {
        return `${origin}${fallbackImagePath}`;
      }
    }

    const normalizedPath = value.startsWith("/") ? value : `/${value}`;
    return toSiteUrl(normalizedPath, origin);
  }

  return {
    fallbackImagePath,
    humanizeSlugLabel,
    normalizeMetaText,
    toAbsolutePublicUrl,
  };
}
